import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { reportService } from '../../services/reportService';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const PAYMENT_METHOD_LABELS = {
  trust_funds: 'Trust',
  check: 'Check',
  wire: 'Wire',
  credit_card: 'Credit Card',
  cash: 'Cash',
  ach: 'ACH',
};

/**
 * Side panel showing the underlying invoices/payments behind a single
 * Revenue report cell. Opened by clicking a Billed or Collected cell.
 */
const RevenueDrillDownPanel = ({ isOpen, onClose, context }) => {
  // context: { case_id, case_number, case_title, client_name, type, column, start_date, end_date, attorney_id }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!context) return;
    setLoading(true);
    setError(null);
    try {
      const res = await reportService.getRevenueDrillDown({
        case_id: context.case_id,
        type: context.type,
        start_date: context.start_date,
        end_date: context.end_date,
        attorney_id: context.attorney_id || undefined,
      });
      setData(res.data);
    } catch (err) {
      console.error('Drill-down failed:', err);
      setError('Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    if (isOpen && context) fetchData();
  }, [isOpen, context, fetchData]);

  if (!isOpen || !context) return null;

  // Filter billed items by column subtype if requested (time / hours / expense)
  const filteredBilled = (() => {
    if (!data || data.type !== 'billed') return [];
    if (context.column === 'billed_expense') return data.items.filter(i => i.item_type === 'expense');
    if (context.column === 'billed_time' || context.column === 'billed_hours')
      return data.items.filter(i => i.item_type === 'time');
    return data.items;
  })();

  const filteredCollected = data?.type === 'collected' ? data.items : [];

  const totalBilled = filteredBilled.reduce((s, i) => s + (i.amount || 0), 0);
  const totalBilledHours = filteredBilled.reduce((s, i) => s + (i.hours || 0), 0);
  const totalCollected = filteredCollected.reduce((s, i) => s + (i.attributed_amount || 0), 0);

  const headerLabel = (() => {
    switch (context.column) {
      case 'billed_time': return 'Billed Time';
      case 'billed_hours': return 'Billed Hours';
      case 'billed_expense': return 'Billed Expenses';
      case 'collected_time': return 'Collected Time';
      case 'collected_expense': return 'Collected Expenses';
      default: return context.type === 'billed' ? 'Billed' : 'Collected';
    }
  })();

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-5 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{headerLabel}</p>
              <h2 className="text-lg font-bold text-gray-900 mt-1">{context.client_name}</h2>
              <p className="text-sm text-gray-600">
                {context.case_number}
                {context.case_title ? ` — ${context.case_title}` : ''}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(context.start_date)} – {formatDate(context.end_date)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-50 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="animate-pulse space-y-3">
              <div className="h-12 bg-gray-100 rounded" />
              <div className="h-12 bg-gray-100 rounded" />
              <div className="h-12 bg-gray-100 rounded" />
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-8">
              <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
              <p className="text-gray-500">{error}</p>
              <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
            </div>
          )}

          {!loading && !error && data?.type === 'billed' && (
            filteredBilled.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No line items in this period.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hrs</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredBilled.map((item) => (
                    <tr key={item.line_item_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm text-gray-900">
                        <div>{item.invoice_number}</div>
                        <div className="text-xs text-gray-500">{formatDate(item.invoice_date)}</div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        <div className="line-clamp-2">{item.description}</div>
                        {item.attorney_name && (
                          <div className="text-xs text-gray-500">{item.attorney_name}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                        {item.hours != null ? item.hours.toFixed(2) : '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900 font-medium">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-2 text-sm" colSpan={2}>Total</td>
                    <td className="px-3 py-2 text-sm text-right">{totalBilledHours.toFixed(2)}</td>
                    <td className="px-3 py-2 text-sm text-right">{formatCurrency(totalBilled)}</td>
                  </tr>
                </tbody>
              </table>
            )
          )}

          {!loading && !error && data?.type === 'collected' && (
            filteredCollected.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No payments in this period.</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  Amounts attributed proportionally to {context.attorney_id ? 'the selected attorney' : 'all attorneys'} based on each invoice's line items.
                </p>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payment</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Attributed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredCollected.map((item, idx) => (
                      <tr key={`${item.event_kind}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900">
                          <div>{item.invoice_number}</div>
                          <div className="text-xs text-gray-500">{formatDate(item.event_date)}</div>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {PAYMENT_METHOD_LABELS[item.payment_method] || item.payment_method || '—'}
                          {item.reference_number && (
                            <div className="text-xs text-gray-500">#{item.reference_number}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-gray-700">
                          {formatCurrency(item.payment_amount)}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-green-700 font-medium">
                          {formatCurrency(item.attributed_amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-3 py-2 text-sm" colSpan={3}>Total Attributed</td>
                      <td className="px-3 py-2 text-sm text-right text-green-700">{formatCurrency(totalCollected)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default RevenueDrillDownPanel;
